import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/vimbal_logo_h_earth.svg";
import { colors, fonts, media, copperGradient } from "../styles/theme";
import { nav, links } from "../pages/landingContent";
import { NEW_HOME } from "../siteConfig";
import { verticals } from "../pages/verticalContent";

const Bar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: ${(p) => (p.$scrolled ? "10px 0" : "18px 0")};
  transition: padding 0.4s ease;
`;

const Shell = styled.nav`
  margin: 0 auto;
  width: calc(100% - 24px);
  max-width: 1240px;
  height: 60px;
  padding: 0 10px 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$scrolled ? "rgba(214, 202, 184, 0.9)" : "transparent")};
  background: ${(p) => (p.$scrolled ? "rgba(250, 247, 241, 0.8)" : "transparent")};
  backdrop-filter: ${(p) => (p.$scrolled ? "saturate(140%) blur(16px)" : "none")};
  -webkit-backdrop-filter: ${(p) => (p.$scrolled ? "saturate(140%) blur(16px)" : "none")};
  box-shadow: ${(p) => (p.$scrolled ? "0 12px 40px -24px rgba(42, 35, 30, 0.45)" : "none")};
  transition: all 0.4s ease;
`;

const Brand = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;

  img.light {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    filter: brightness(0) invert(1);
    pointer-events: none;
  }
  img {
    height: 38px;
    width: auto;

    @media ${media.medium} {
      height: 42px;
    }
  }
`;

const Links = styled.div`
  display: none;
  gap: 4px;

  @media ${media.medium} {
    display: flex;
  }
`;

const NavLink = styled(Link)`
  font-family: ${fonts.body};
  font-size: 15px;
  font-weight: 500;
  color: var(--nav-ink, ${colors.bark});
  padding: 8px 14px;
  border-radius: 999px;
  transition: color 0.25s ease, background-color 0.25s ease;

  &:hover {
    color: ${colors.ink};
    background: rgba(214, 202, 184, 0.45);
  }
`;

// Sits beside the main call to action, quieter so it doesn't compete with it.
const LoginLink = styled.a`
  display: none;
  align-items: center;
  height: 42px;
  padding: 0 14px;
  font-family: ${fonts.body};
  font-size: 15px;
  font-weight: 600;
  color: var(--nav-ink, ${colors.bark});
  transition: color 0.25s ease;

  &:hover {
    color: ${colors.copper};
  }

  @media ${media.small} {
    display: inline-flex;
  }
`;

const Cta = styled(Link)`
  display: none;
  align-items: center;
  height: 42px;
  padding: 0 20px;
  border-radius: 999px;
  font-family: ${fonts.body};
  font-size: 15px;
  font-weight: 600;
  color: ${colors.linen};
  background: ${copperGradient};
  box-shadow: 0 8px 20px -10px rgba(143, 83, 41, 0.8);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-1px);
  }

  @media ${media.small} {
    display: inline-flex;
  }
`;

const Menu = styled.div`
  position: relative;
`;

const MenuTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${fonts.body};
  font-size: 15px;
  font-weight: 500;
  color: var(--nav-ink, ${colors.bark});
  padding: 8px 14px;
  border-radius: 999px;
  background: ${(p) => (p.$open ? "rgba(214, 202, 184, 0.45)" : "transparent")};
  cursor: pointer;

  &:hover {
    color: ${colors.ink};
    background: rgba(214, 202, 184, 0.45);
  }
  svg {
    transition: transform 0.25s ease;
    transform: rotate(${(p) => (p.$open ? "180deg" : "0deg")});
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  width: 340px;
  padding: 10px;
  border-radius: 22px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};
  box-shadow: 0 30px 60px -30px rgba(42, 35, 30, 0.45);
  opacity: ${(p) => (p.$open ? 1 : 0)};
  visibility: ${(p) => (p.$open ? "visible" : "hidden")};
  transform: translate(-50%, ${(p) => (p.$open ? "0" : "-6px")});
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;

  /* invisible bridge so the menu stays open while the pointer crosses the gap */
  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: -14px;
    height: 14px;
  }

  a {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 14px;
    font-family: ${fonts.display};
    font-size: 20px;
    letter-spacing: -0.015em;
    color: ${colors.ink};
  }
  a:hover,
  a[aria-current="page"] {
    background: ${colors.limestone};
  }
  a i {
    flex: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }
  a small {
    display: block;
    font-family: ${fonts.body};
    font-size: 13px;
    letter-spacing: 0;
    color: ${colors.pebble};
  }
`;

const Chevron = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MenuButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: ${colors.ink};
  cursor: pointer;
  display: grid;
  place-content: center;
  gap: 5px;

  span {
    display: block;
    width: 16px;
    height: 1.5px;
    background: ${colors.linen};
    transition: transform 0.3s ease;
  }
  &[aria-expanded="true"] span:first-child {
    transform: translateY(3.25px) rotate(45deg);
  }
  &[aria-expanded="true"] span:last-child {
    transform: translateY(-3.25px) rotate(-45deg);
  }

  @media ${media.medium} {
    display: none;
  }
`;

const Sheet = styled.div`
  position: fixed;
  inset: 0;
  z-index: -1;
  padding: 110px 28px 40px;
  background: ${colors.limestone};
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  visibility: ${(p) => (p.$open ? "visible" : "hidden")};
  transition: opacity 0.35s ease, visibility 0.35s ease;

  overflow-y: auto;

  p {
    margin-top: 16px;
    font-family: ${fonts.mono};
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.pebble};
  }
  a {
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: 34px;
    letter-spacing: -0.03em;
    color: ${colors.ink};
    padding: 6px 0;
    border-bottom: 1px solid ${colors.stone};
  }
  a:last-child {
    color: ${colors.copper};
    font-style: normal;
    border: 0;
  }

  @media ${media.medium} {
    display: none;
  }
`;

// Home and How it works bar: clear with light text over the dark opening section, filling to the usual cream pill with dark
// text as the visitor scrolls (Josh, 29 Sep 2026).
const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t)).join(", ");
const glassStyle = (f) => ({
  background: `rgba(250, 247, 241, ${0.8 * f})`,
  borderColor: `rgba(214, 202, 184, ${0.9 * f})`,
  boxShadow: `0 12px 40px -24px rgba(42, 35, 30, ${0.45 * f})`,
  backdropFilter: f > 0.02 ? `saturate(${100 + 40 * f}%) blur(${16 * f}px)` : "none",
  WebkitBackdropFilter: f > 0.02 ? `saturate(${100 + 40 * f}%) blur(${16 * f}px)` : "none",
  "--nav-ink": `rgb(${mix([250, 247, 241], [91, 77, 66], f)})`,
});

const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [fill, setFill] = useState(0);
  const [open, setOpen] = useState(false);
  const [solutions, setSolutions] = useState(false);
  const menuRef = useRef(null);
  const hoverOpenedAt = useRef(0);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      // Home page: the bar starts clear over the dark hero and fills in over the first scroll.
      const f = Math.min(1, Math.max(0, window.scrollY / 260));
      setFill((prev) => (Math.abs(prev - f) > 0.01 || f === 0 || f === 1 ? f : prev));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close menus whenever the route changes.
  useEffect(() => {
    setOpen(false);
    setSolutions(false);
  }, [pathname, hash]);

  // Close the dropdown on outside click or Escape.
  useEffect(() => {
    if (!solutions) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setSolutions(false);
    };
    const onKey = (e) => e.key === "Escape" && setSolutions(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [solutions]);

  const current = (to) => (pathname === to ? "page" : undefined);
  // Dark hero pages need the solid bar from the start so the links stay legible.
  const glass = ((NEW_HOME && (pathname === "/" || pathname === "/draft")) || pathname === "/how-it-works") && !open;
  const darkTop = pathname === "/future" || verticals.some((v) => v.dark && pathname === `/${v.slug}`);
  const dot = (v) => (v.dark ? colors.forest : v.accent);

  return (
    <Bar $scrolled={scrolled || open}>
      <Shell $scrolled={!glass && (scrolled || darkTop) && !open} style={glass ? glassStyle(fill) : undefined} aria-label="Main">
        <Brand to="/" aria-label="vimbal home">
          <img src={logo} alt="vimbal" />
          {glass && <img className="light" src={logo} alt="" aria-hidden="true" style={{ opacity: 1 - fill }} />}
        </Brand>
        <Links>
          <NavLink to="/" aria-current={current("/")}>
            Home
          </NavLink>
          <NavLink to={nav[0].to} aria-current={current(nav[0].to)}>
            {nav[0].label}
          </NavLink>
          <Menu ref={menuRef} onMouseLeave={() => setSolutions(false)}>
            <MenuTrigger
              type="button"
              $open={solutions}
              aria-expanded={solutions}
              aria-controls="solutions-menu"
              onClick={() => {
                // A click right after hover-open should not immediately close the menu.
                if (Date.now() - hoverOpenedAt.current < 500) return;
                setSolutions((o) => !o);
              }}
              onMouseEnter={() => {
                if (!solutions) hoverOpenedAt.current = Date.now();
                setSolutions(true);
              }}
            >
              Who it's for <Chevron />
            </MenuTrigger>
            <Dropdown id="solutions-menu" $open={solutions}>
              {verticals.map((v) => (
                <Link key={v.slug} to={`/${v.slug}`} aria-current={current(`/${v.slug}`)} tabIndex={solutions ? 0 : -1}>
                  <i style={{ background: dot(v) }} aria-hidden="true" />
                  <span>
                    {v.nav}
                    <small>
                      {v.titleLead} {v.titleEmphasis}
                    </small>
                  </span>
                </Link>
              ))}
            </Dropdown>
          </Menu>
          {nav.slice(1).map((item) => (
            <NavLink key={item.to} to={item.to} aria-current={current(item.to)}>
              {item.label}
            </NavLink>
          ))}
        </Links>
        <Right>
          {links.login && (
            <LoginLink as={links.login.startsWith("/") ? Link : "a"} {...(links.login.startsWith("/") ? { to: links.login } : { href: links.login, rel: "noopener noreferrer" })}>
              {links.loginLabel}
            </LoginLink>
          )}
          <Cta to={links.register}>Register interest</Cta>
          <MenuButton
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            <span />
            <span />
          </MenuButton>
        </Right>
      </Shell>
      <Sheet $open={open}>
        <p>Who it's for</p>
        {verticals.map((v) => (
          <Link key={v.slug} to={`/${v.slug}`} tabIndex={open ? 0 : -1}>
            {v.nav}
          </Link>
        ))}
        <p>vimbal</p>
        <Link to="/" tabIndex={open ? 0 : -1}>
          Home
        </Link>
        {nav.map((item) => (
          <Link key={item.to} to={item.to} tabIndex={open ? 0 : -1}>
            {item.label}
          </Link>
        ))}
        <Link to={links.register} tabIndex={open ? 0 : -1}>
          Register interest
        </Link>
        {links.login && (
          <Link to={links.login} tabIndex={open ? 0 : -1}>
            {links.loginLabel}
          </Link>
        )}
      </Sheet>
    </Bar>
  );
};

export default NavBar;
